/**
 * Auth store — manages user authentication state.
 *
 * Uses Zustand for lightweight state management.
 * Stores tokens in localStorage via api.ts token helpers.
 */

import { create } from 'zustand';
import { api, setAccessToken, getAccessToken, getRefreshToken, setRefreshToken, setOnLogout } from './api.js';
import type { AuthTokens } from '@dentai/shared';
import type { PendingEmailVerificationResponse } from './api.js';
import { currentLocale, localizeErrorCode } from './error-localization.js';

export interface AuthUser {
  userId: number;
  email: string;
  name: string | null;
  role: string;
  permissions: string[];
  organizationId: number | null;
  clinicId: number | null;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  verificationRequiredEmail: string | null;
  login: (email: string, password: string, mfaCode?: string) => Promise<void>;
  register: (input: {
    email: string;
    password: string;
    name: string;
    organizationName?: string;
    clinicName?: string;
    timezone?: string;
    plan?: 'free' | 'trial';
  }) => Promise<PendingEmailVerificationResponse>;
  logout: () => Promise<void>;
  clearError: () => void;
  initialize: () => void;
}

function storeTokens(tokens: AuthTokens): void {
  setAccessToken(tokens.accessToken);
  setRefreshToken(tokens.refreshToken);
}

function clearTokens(): void {
  setAccessToken(null);
  setRefreshToken(null);
}

// Decode JWT payload (without verification — just to get user info)
function decodeJwt(token: string): { sub: number; email?: string; name?: string; role?: string; permissions?: string[]; org_id?: number; clinic_id?: number } | null {
  try {
    const payload = token.split('.')[1] ?? '';
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  verificationRequiredEmail: null,

  login: async (email, password, mfaCode) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.auth.login(email, password, mfaCode);
      if (res.error) {
        if (res.error.code === 'EMAIL_VERIFICATION_REQUIRED') {
          set({ verificationRequiredEmail: email });
          throw new Error(localizeErrorCode('EMAIL_VERIFICATION_REQUIRED', currentLocale()));
        }
        throw new Error(res.error.message);
      }
      if (!res.data) {
        throw new Error(localizeErrorCode('INTERNAL_ERROR', currentLocale()));
      }
      storeTokens(res.data.tokens);
      set({
        user: { userId: res.data.user.id, email: res.data.user.email, name: res.data.user.name ?? null, role: res.data.user.role, permissions: decodeJwt(res.data.tokens.accessToken)?.permissions ?? [], organizationId: res.data.user.organizationId, clinicId: res.data.user.clinicId },
        isAuthenticated: true,
        isLoading: false,
        error: null,
        verificationRequiredEmail: null,
      });
    } catch (err) {
      clearTokens();
      set({ isLoading: false, error: (err as Error).message, isAuthenticated: false, user: null });
      throw err;
    }
  },

  register: async (input) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.auth.register(input);
      if (res.error) {
        throw new Error(res.error.message);
      }
      if (!res.data) {
        throw new Error(localizeErrorCode('INTERNAL_ERROR', currentLocale()));
      }
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        verificationRequiredEmail: res.data.email,
      });
      return res.data;
    } catch (err) {
      clearTokens();
      set({ isLoading: false, error: (err as Error).message, isAuthenticated: false, user: null });
      throw err;
    }
  },

  logout: async () => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        await api.auth.logout(refreshToken);
      } catch {
        // Ignore errors — we're logging out anyway
      }
    }
    clearTokens();
    set({ user: null, isAuthenticated: false, error: null, isLoading: false, verificationRequiredEmail: null });
  },

  clearError: () => set({ error: null }),

  initialize: () => {
    const token = getAccessToken();
    if (token) {
      const payload = decodeJwt(token);
      if (payload) {
        set({
          user: { userId: payload.sub, email: payload.email ?? '', name: payload.name ?? null, role: payload.role ?? 'unknown', permissions: payload.permissions ?? [], organizationId: payload.org_id ?? null, clinicId: payload.clinic_id ?? null },
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        clearTokens();
        set({ user: null, isAuthenticated: false, isLoading: false });
      }
    } else {
      set({ isLoading: false });
    }
  },
}));

// Register a callback so the API client can trigger logout on session expiry
setOnLogout(() => {
  useAuthStore.getState().logout();
  window.location.hash = '/login';
});
